import { ReactElement, ReactNode } from 'react';

import { Box, Circle, Flex, IconButton, Tooltip, useColorModeValue } from '@bonusly/design-system';

import { BizyProgressiveTopBlur } from './components/bizy-progressive-top-blur';

interface BizySidePanelHeaderProps {
	left: ReactNode;
	pl?: number;
	right?: ReactNode;
	/**
	 * Overlay the scroll surface so timeline content can pass behind the header,
	 * adding the progressive blur/fade and the compact overlay geometry.
	 */
	overlay?: boolean;
	// Desktop MFE (Electron): make the header bar a frameless-window drag region.
	// Off by default so the web side panel is unchanged.
	isDragRegion?: boolean;
}

export const BIZY_CHAT_HEADER_HEIGHT_PX = 60;
export const BIZY_CHAT_HEADER_HEIGHT = `${BIZY_CHAT_HEADER_HEIGHT_PX}px`;
/** Gap left below the overlay header when anchoring content to the top of the scrollport. */
export const BIZY_CHAT_SCROLL_GAP_PX = 36;
/**
 * Distance from the scrollport top at which anchored content (the just-sent
 * message, the fresh-start divider, focus scrolls) clears the overlay header.
 */
export const BIZY_CHAT_SCROLL_TOP_INSET_PX = BIZY_CHAT_HEADER_HEIGHT_PX + BIZY_CHAT_SCROLL_GAP_PX;

/**
 * The panel paints raw white / gray.900 rather than `bg.surface`, because custom
 * and seasonal themes repaint the `bg.*` tokens. Anything that has to match the
 * panel exactly — the header's fade, the unread dot's ring — has to read the
 * same raw values, or it lands a shade off under those themes.
 */
const usePanelSurfaceColor = () => useColorModeValue('white', 'gray.900');

export function BizySidePanelHeader({ left, pl = 5, right, overlay = false, isDragRegion = false }: BizySidePanelHeaderProps) {
	const panelSurface = usePanelSurfaceColor();

	return (
		<Flex
			position={overlay ? 'absolute' : 'relative'}
			top={overlay ? 0 : undefined}
			left={overlay ? 0 : undefined}
			right={overlay ? 0 : undefined}
			align="center"
			justify="space-between"
			gap={0}
			pl={pl}
			pr={3}
			py={overlay ? 3 : { base: 4, sm: 3 }}
			flexShrink={0}
			minH={BIZY_CHAT_HEADER_HEIGHT}
			zIndex={overlay ? 2 : undefined}
			pointerEvents={overlay ? 'none' : undefined}
			sx={isDragRegion ? { WebkitAppRegion: 'drag' } : undefined}
		>
			{overlay && <BizyProgressiveTopBlur surfaceColor={panelSurface} />}
			{/* Keep the empty stretch transparent to the timeline while the
			    shrink-wrapped painted content absorbs pointer events. */}
			<Box minW={0} flex={1} overflow="hidden" position="relative" zIndex={1}>
				<Box display="inline-flex" maxW="100%" minW={0} pointerEvents={overlay ? 'auto' : undefined} data-component="bizy-header-left-content">
					{left}
				</Box>
			</Box>
			{right && (
				<Box
					display="inline-flex"
					maxW="100%"
					minW={0}
					flexShrink={0}
					position="relative"
					zIndex={1}
					pointerEvents={overlay ? 'auto' : undefined}
					data-component="bizy-header-right-content"
				>
					{right}
				</Box>
			)}
		</Flex>
	);
}

interface BizyHeaderIconButtonProps {
	label: string;
	icon: ReactElement;
	onClick: () => void;
	color?: string;
	// Desktop MFE (Electron): exclude this control from the header drag region so
	// it stays clickable. Off by default so the web side panel is unchanged.
	isNoDrag?: boolean;
	/** Unread dot on the icon's top-right — mirrors the blips inside the menu. */
	hasUnreadIndicator?: boolean;
}

// The panel headers' standard tooltipped icon control (menu, minimize, close,
// settings, ...). The label doubles as the aria-label.
export function BizyHeaderIconButton({
	label,
	icon,
	onClick,
	color = 'secondary.fg',
	isNoDrag = false,
	hasUnreadIndicator = false,
}: BizyHeaderIconButtonProps) {
	const panelSurface = usePanelSurfaceColor();

	return (
		<Tooltip label={label} hasArrow={false}>
			<IconButton
				aria-label={label}
				icon={
					hasUnreadIndicator ? (
						<Box position="relative" display="inline-flex">
							{icon}
							<Circle
								position="absolute"
								top="-2px"
								right="-2px"
								size="8px"
								bg="primary.fg"
								outline="2px solid"
								outlineColor={panelSurface}
								data-testid="bizy-header-unread-dot"
							/>
						</Box>
					) : (
						icon
					)
				}
				variant="ghost"
				size="sm"
				boxSize="32px"
				color={color}
				_hover={{ bg: 'secondary.bgAlpha' }}
				onClick={onClick}
				sx={isNoDrag ? { WebkitAppRegion: 'no-drag' } : undefined}
			/>
		</Tooltip>
	);
}
